/*
  Progressive enhancement for the K-Trending collections carousel.

  Swiping, snapping and scrolling are all native CSS — this only adds the dot
  indicators, and only while the row actually overflows (i.e. below 990px).
  With JS off, the carousel still works; you just don't get dots.
*/

if (!customElements.get('k-trending-room')) {
  customElements.define(
    'k-trending-room',
    class KTrendingRoom extends HTMLElement {
      connectedCallback() {
        this.media = this.querySelector('.k-trending__room-media');
        if (!this.media) return;
        if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        // must stay within the CSS bleed on .k-trending__room-media (4rem)
        this.range = 22;
        this.ticking = false;
        this.onScroll = this.onScroll.bind(this);
        this.update = this.update.bind(this);

        // only listen to scroll while the room is actually near the
        // viewport, so idle sections elsewhere on the page cost nothing
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) {
              addEventListener('scroll', this.onScroll, { passive: true });
              this.update();
            } else {
              removeEventListener('scroll', this.onScroll);
            }
          },
          { rootMargin: '25% 0px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        removeEventListener('scroll', this.onScroll);
        if (this.observer) this.observer.disconnect();
        if (this.frame) cancelAnimationFrame(this.frame);
      }

      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        this.frame = requestAnimationFrame(() => {
          this.ticking = false;
          this.update();
        });
      }

      update() {
        const rect = this.getBoundingClientRect();
        const center = rect.top + rect.height / 2;
        const travel = innerHeight / 2 + rect.height / 2;
        const progress = travel > 0 ? (center - innerHeight / 2) / travel : 0;
        const clamped = Math.max(-1, Math.min(1, progress));
        this.media.style.transform = `translate3d(0, ${(clamped * this.range * -1).toFixed(1)}px, 0)`;
      }
    }
  );
}

if (!customElements.get('k-trending-carousel')) {
  customElements.define(
    'k-trending-carousel',
    class KTrendingCarousel extends HTMLElement {
      connectedCallback() {
        this.row = this.querySelector('.k-trending__row');
        this.dots = this.querySelector('.k-trending__dots');
        if (!this.row || !this.dots) return;

        this.cells = Array.from(this.row.querySelectorAll('.k-trending__cell'));
        if (this.cells.length < 2) return;

        this.activeIndex = -1;
        // reset in case the theme editor re-inserted this element mid-frame
        this.frame = null;
        this.onScroll = this.onScroll.bind(this);
        this.sync = this.sync.bind(this);

        this.row.addEventListener('scroll', this.onScroll, { passive: true });

        this.resizeObserver = new ResizeObserver(this.sync);
        this.resizeObserver.observe(this.row);

        this.sync();
      }

      disconnectedCallback() {
        if (this.row) this.row.removeEventListener('scroll', this.onScroll);
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = null;
      }

      get isScrollable() {
        return this.row.scrollWidth - this.row.clientWidth > 4;
      }

      get startPadding() {
        return parseFloat(getComputedStyle(this.row).paddingInlineStart) || 0;
      }

      sync() {
        if (!this.isScrollable) {
          this.dots.hidden = true;
          return;
        }

        if (this.dots.childElementCount !== this.cells.length) this.build();
        this.dots.hidden = false;
        this.update();
      }

      build() {
        const fragment = document.createDocumentFragment();

        this.cells.forEach((cell, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'k-trending__dot';
          dot.setAttribute('aria-label', cell.dataset.label || String(index + 1));
          dot.addEventListener('click', () => this.scrollToCell(cell));
          fragment.appendChild(dot);
        });

        this.dots.replaceChildren(fragment);
      }

      scrollToCell(cell) {
        const offset = cell.getBoundingClientRect().left - this.row.getBoundingClientRect().left;
        this.row.scrollTo({
          left: this.row.scrollLeft + offset - this.startPadding,
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      }

      onScroll() {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      }

      update() {
        const atEnd = this.row.scrollLeft + this.row.clientWidth >= this.row.scrollWidth - 2;
        let index = this.cells.length - 1;

        if (!atEnd) {
          const edge = this.row.getBoundingClientRect().left + this.startPadding;
          let closest = Infinity;

          this.cells.forEach((cell, i) => {
            const distance = Math.abs(cell.getBoundingClientRect().left - edge);
            if (distance < closest) {
              closest = distance;
              index = i;
            }
          });
        }

        if (index === this.activeIndex) return;
        this.activeIndex = index;

        Array.from(this.dots.children).forEach((dot, i) => {
          if (i === index) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }
    }
  );
}
